import { useEffect } from "react";
import { MenuItem, TextField } from "@mui/material";
import { VehicleStrutureCatalog } from "@interfaces/catalogs";
import { AssetVehicleValues } from "@interfaces/seleccionActivo";
import { LibroAzul } from "@hooks/useLibroAzul";
import { FieldWrapper, selectLoadingProps } from "../FieldWrapper";

interface SelectOption {
  value: string;
  label: string;
}

const toOptions = (items: VehicleStrutureCatalog[]): SelectOption[] =>
  items.map((item) => ({ value: item.code, label: item.name }));

interface Props {
  /** Autos y SUVs: año, marca, modelo y versión del Libro Azul. Los demás vehículos se capturan en texto. */
  auto: boolean;
  values: AssetVehicleValues;
  libroAzul: LibroAzul;
  onChange: (patch: Partial<AssetVehicleValues>) => void;
}

/**
 * Datos del vehículo de `SeleccionActivo`, con el año primero. Va dentro de la cuadrícula de dos columnas de la
 * pantalla. En autos cada select se habilita al elegir el anterior y limpia todo lo que depende de él, como la
 * garantía prendaria; los demás vehículos van en texto, como su captura manual.
 */
export const FormActivoVehiculo = ({ auto, values, libroAzul, onChange }: Props) => {
  useEffect(() => {
    if (auto) libroAzul.loadYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto]);

  const selectField = (
    field: keyof AssetVehicleValues,
    label: string,
    options: SelectOption[],
    onSelect: (value: string) => void,
    dependsOn?: { value: string; label: string },
  ) => {
    // Los campos se llaman igual que su catálogo; el año no tiene spinner porque se carga una sola vez.
    const loading = libroAzul.loading === field;
    const enabled = !dependsOn || Boolean(dependsOn.value);
    return (
      <FieldWrapper label={label} loading={loading}>
        <TextField
          autoComplete="off"
          size="lendia_input"
          select
          fullWidth
          value={values[field]}
          onChange={(event) => onSelect(event.target.value)}
          disabled={loading || !enabled}
          SelectProps={selectLoadingProps(loading)}
          inputProps={{ "data-testid": `activo-${field}-select` }}
        >
          <MenuItem value="">{enabled ? "Selecciona…" : `Elige ${dependsOn?.label}`}</MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </FieldWrapper>
    );
  };

  const textField = (field: "brand" | "model" | "version", label: string, placeholder: string) => (
    <FieldWrapper label={label}>
      <TextField
        autoComplete="off"
        size="lendia_input"
        fullWidth
        value={values[field]}
        onChange={(event) => onChange({ [field]: event.target.value })}
        placeholder={placeholder}
        inputProps={{ "data-testid": `activo-${field}-input` }}
      />
    </FieldWrapper>
  );

  if (!auto) {
    return (
      <>
        <FieldWrapper label="Año">
          <TextField
            autoComplete="off"
            size="lendia_input"
            fullWidth
            value={values.year}
            onChange={(event) => {
              if (/^\d{0,4}$/.test(event.target.value)) onChange({ year: event.target.value });
            }}
            placeholder="Ej. 2021"
            inputProps={{ maxLength: 4, inputMode: "numeric", "data-testid": "activo-year-input" }}
          />
        </FieldWrapper>
        {textField("brand", "Marca", "Ej. Mercedes-Benz")}
        {textField("model", "Modelo", "Ej. Sprinter")}
        {textField("version", "Versión", "Ej. Base")}
      </>
    );
  }

  return (
    <>
      {selectField(
        "year",
        "Año",
        libroAzul.years.map((year) => ({ value: year, label: year })),
        (year) => {
          onChange({ year, brand: "", model: "", version: "" });
          libroAzul.loadBrands(year);
        },
      )}
      {selectField(
        "brand",
        "Marca",
        toOptions(libroAzul.brands),
        (brand) => {
          onChange({ brand, model: "", version: "" });
          libroAzul.loadModels(values.year, brand);
        },
        { value: values.year, label: "año" },
      )}
      {selectField(
        "model",
        "Modelo",
        toOptions(libroAzul.models),
        (model) => {
          onChange({ model, version: "" });
          libroAzul.loadVersions(values.year, values.brand, model);
        },
        { value: values.brand, label: "marca" },
      )}
      {selectField("version", "Versión", toOptions(libroAzul.versions), (version) => onChange({ version }), {
        value: values.model,
        label: "modelo",
      })}
    </>
  );
};
